import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import Loader from "./Loader";
import { useSession } from "../SessionContext";
import * as XLSX from 'xlsx';
import {
  IconReportAnalytics,
  IconSearch,
  IconDownload,
  IconInfoCircle,
  IconX,
  IconSortAscending,
  IconSortDescending,
  IconCalendar,
  IconHelp,
} from "@tabler/icons-react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { rolplaySteps } from "../utils/tutorial";

//const IP = "http://localhost:8000";
//const IP = "http://localhost:5020";
const IP = "https://reportes.aux-rolplay.com";

const RolplaySalinas = () => {
  const { id_user, key } = useSession();
  const [realizadasData, setRealizadasData] = useState([]);
  const [noRealizadasData, setNoRealizadasData] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("Realizadas");
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState("");
  const [showInfo, setShowInfo] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activeStartDate, setActiveStartDate] = useState("");
  const [activeEndDate, setActiveEndDate] = useState("");
  const rowsPerPage = 20;

  const realizadasColumns = useMemo(
    () => [
      { key: "admin_name", label: "Gerente", align: "left" },
      { key: "reg_user_access", label: "Usuario", align: "left" },
      { key: "reg_user_name", label: "Nombre Usuario", align: "left" },
      { key: "reg_activity_name", label: "Actividad", align: "center" },
      { key: "reg_exercise_name", label: "Ejercicio", align: "center" },
      { key: "reg_robin_rubric_score", label: "Rúbrica", align: "left" },
      {
        key: "reg_robin_facial_score",
        label: "Facial (Calificación)",
        align: "left",
      },
      {
        key: "reg_robin_voice_score",
        label: "Voz (Calificación)",
        align: "left",
      },
      {
        key: "reg_robin_ppm_score",
        label: "Palabras / Min (Calificación)",
        align: "left",
      },
      ...(key === "Ko3a244ASp"
        ? [
            {
              key: "msi_robin_speech",
              label: "Duración del discurso",
              align: "left",
            },
          ]
        : []),
      { key: "reg_robin_rubric_total", label: "Calificación", align: "left" },
      ...(key === "Ko3a244ASp"
        ? [{ key: "reg_comment", label: "Comentarios", align: "center" }]
        : []),
      { key: "reg_datetime", label: "Fecha Grabación", align: "left" },
    ],
    [key],
  );

  const noRealizadasColumns = useMemo(
    () => [
      { key: "assign_user_name", label: "Usuario", align: "left" },
      { key: "user_branch", label: "Sucursal", align: "left" },
      { key: "user_profile", label: "Puesto", align: "left" },
      { key: "assign_title", label: "Titulo", align: "left" },
    ],
    [],
  );

  useEffect(() => {
    if (id_user && key) {
      fetch(`${IP}/salinas/rolplay`)
        .then((response) => response.json())
        .then((data) => {
          setRealizadasData(data.result.Realizadas);
          setNoRealizadasData(data.result["No realizadas"]);
          setRowData(data.result.Realizadas);
          setIsLoading(false);
          toast.success("Datos cargados correctamente");
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          setIsLoading(false);
          toast.error("Error al cargar los datos");
        });
    }
  }, [id_user, key]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearchText("");
    setCurrentPage(1);
    setSortConfig({ key: null, direction: "asc" });
  };

  const onFilterTextChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const sortData = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  // Mover las funciones auxiliares al inicio
  const formatDBDate = (dateString) => {
    if (!dateString) return null;
    return dateString.split(" ")[0];
  };

  const filterByDateRange = (data) => {
    if (!activeStartDate && !activeEndDate) return data;

    return data.filter((row) => {
      const dateField =
        activeTab === "Realizadas" ? "reg_datetime" : "assign_datetime";
      const rowDate = formatDBDate(row[dateField]);
      if (!rowDate) return false;

      if (activeStartDate && activeEndDate) {
        return rowDate >= activeStartDate && rowDate <= activeEndDate;
      } else if (activeStartDate) {
        return rowDate >= activeStartDate;
      } else if (activeEndDate) {
        return rowDate <= activeEndDate;
      }
      return true;
    });
  };

  // Ahora filteredData puede usar filterByDateRange
  const filteredData = useMemo(() => {
    const data = activeTab === "Realizadas" ? realizadasData : noRealizadasData;
    let filtered =
      data?.filter((item) =>
        Object.values(item).some(
          (val) =>
            typeof val === "string" &&
            val.toLowerCase().includes(searchText.toLowerCase()),
        ),
      ) || [];
    return filterByDateRange(filtered);
  }, [
    realizadasData,
    noRealizadasData,
    searchText,
    activeTab,
    activeStartDate,
    activeEndDate,
  ]);

  const sortedData = useMemo(() => {
    const data = [...filteredData];
    if (sortConfig.key) {
      data.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return data;
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedData.slice(start, end);
  }, [sortedData, currentPage, rowsPerPage]);

  // Add download functionality
  const onExportClick = () => {
    try {
      const excelContent = convertToExcel(filteredData);
      downloadExcel(excelContent, `${activeTab}_rolplay_data.xlsx`);
      toast.success("Archivo descargado correctamente", {
        description: `Se exportaron ${filteredData.length} registros`,
      });
    } catch (error) {
      toast.error("Error al descargar el archivo", {
        description: "Por favor, intenta nuevamente",
      });
    }
  };

  const convertToExcel = (data) => {
    const currentColumns =
      activeTab === "Realizadas" ? realizadasColumns : noRealizadasColumns;
    const header = currentColumns.map((col) => col.label);
    const rows = data.map((row) =>
      currentColumns.map((col) => {
        const value = row[col.key];
        // Convertir explícitamente valores nulos, undefined o vacíos a "0"
        if (value === null || value === undefined || value === "") {
          // Solo convertir a "0" si la columna es numérica
          if (col.key.includes("score") || col.key.includes("rubric")) {
            return "0";
          }
          return "";
        }
        return value;
      }),
    );

    // Crear workbook y worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    
    // Añadir la hoja al workbook
    XLSX.utils.book_append_sheet(wb, ws, activeTab);
    
    // Generar el archivo Excel
    return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  };

  const downloadExcel = (content, fileName) => {
    const blob = new Blob([content], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Añadir estas funciones para la navegación antes del return
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const goToStart = () => setCurrentPage(1);
  const goToEnd = () => setCurrentPage(totalPages);

  const startTutorial = () => {
    const driverObj = driver({
      showProgress: true,
      steps: rolplaySteps,
      animate: true,
      overlayColor: "#00000099",
    });
    driverObj.drive();
  };

  // Restaurar la condición original del Loader
  if (isLoading) return <Loader />;

  return (
    <div className="w-full p-5">
      <div className="mb-6">
        <div className="p-4 border border-purple-400 bg-purple-50 rounded-xl text-purple-800 text-sm flex items-center gap-2">
          <i className="fas fa-info-circle text-purple-600"></i>
          <p>
            La información mostrada corresponde al histórico completo de uso del
            simulador Rolplay sin exclusiones ni filtros. Para actualizar la
            información mostrada en la tabla, por favor recargue la página
            (Actualizacion cada 30 min).
          </p>
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <div id="rolplay-tabs" className="flex gap-4">
          <button
            onClick={() => switchTab("Realizadas")}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 shadow-md hover:shadow-xl flex items-center gap-2
              ${
                activeTab === "Realizadas"
                  ? "bg-gradient-to-r from-[#dc2626] to-[#991b1b] text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
          >
            <IconReportAnalytics size={18} />
            Tareas Completadas
          </button>
          <button
            onClick={() => switchTab("No realizadas")}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 shadow-md hover:shadow-xl flex items-center gap-2
              ${
                activeTab === "No realizadas"
                  ? "bg-gradient-to-r from-[#dc2626] to-[#991b1b] text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
          >
            <IconReportAnalytics size={18} />
            Tareas Pendientes
          </button>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => setShowInfo(true)} className="relative group">
            <div className="absolute -inset-3 bg-red-500/50 rounded-full blur opacity-75 group-hover:opacity-100 animate-ping"></div>
            <div className="relative w-8 h-8 bg-red-600 rounded-full text-white hover:bg-red-700 transition-colors duration-200 flex items-center justify-center">
              <IconInfoCircle size={18} strokeWidth={2.5} />
            </div>
          </button>
          <button
            onClick={startTutorial}
            className="px-6 py-2.5 bg-[#FB9405] hover:bg-[#e08404] rounded-full text-sm font-medium text-white shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            title="Iniciar Tutorial"
          >
            <IconHelp size={18} />
            Tutorial
          </button>
          <div id="rolplay-search" className="relative">
            <IconSearch
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder={`Buscar ${activeTab === "Realizadas" ? "tarea completada" : "tarea pendiente"}...`}
              value={searchText}
              onChange={onFilterTextChange}
              className="pl-10 pr-4 py-2.5 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1b2a49] focus:border-transparent text-sm w-64 shadow-sm"
            />
          </div>
        </div>
      </div>

      <div
        id="rolplay-table"
        className="w-full rounded-xl overflow-hidden bg-white shadow-lg"
      >
        <div className="overflow-x-auto max-h-[600px] scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-200 hover:scrollbar-thumb-gray-800 scrollbar-thumb-rounded-md scrollbar-track-rounded-md">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-[#991b1b] to-[#dc2626] text-white sticky top-0 z-10">
              <tr>
                {(activeTab === "Realizadas"
                  ? realizadasColumns
                  : noRealizadasColumns
                ).map(({ key, label, align }) => (
                  <th
                    key={key}
                    onClick={() => key !== "actions" && sortData(key)}
                    className={`px-4 py-3 text-[13px] font-medium transition-colors duration-200 hover:bg-white/10 cursor-pointer group ${
                      // Aumentar anchos para actividad y ejercicio
                      key === "reg_activity_name" ||
                      key === "reg_exercise_name" ||
                      key === "assign_title"
                        ? "w-[500px]"
                        : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{label}</span>
                      {key !== "actions" && (
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          {sortConfig.key === key ? (
                            sortConfig.direction === "asc" ? (
                              <IconSortAscending size={16} />
                            ) : (
                              <IconSortDescending size={16} />
                            )
                          ) : (
                            <IconSortAscending
                              size={16}
                              className="opacity-50"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedData.map((row, index) => (
                <tr
                  key={index}
                  className="transition-all duration-200 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 hover:shadow-sm even:bg-gray-50/50"
                >
                  {(activeTab === "Realizadas"
                    ? realizadasColumns
                    : noRealizadasColumns
                  ).map(({ key }) => (
                    <td
                      key={key}
                      className={`px-4 py-2.5 text-[13px] text-gray-600`}
                    >
                      {key === "reg_comment" && row[key] ? (
                        <button
                          onClick={() => {
                            setSelectedComment(row[key]);
                            setShowModal(true);
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#4B5563] text-white text-[13px] rounded-full hover:bg-red-600 transition-colors duration-300"
                        >
                          Ver comentario
                        </button>
                      ) : key === "assign_title" ||
                        key === "reg_activity_name" ||
                        key === "reg_exercise_name" ? (
                        <div className="flex items-center justify-center">
                          <span className="w-full px-6 py-1.5 rounded-full bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-200 font-medium truncate hover:whitespace-normal hover:text-clip">
                            {row[key]}
                          </span>
                        </div>
                      ) : (
                        <span className="whitespace-nowrap">{row[key]}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación - mover aquí, dentro del div de la tabla */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
          <div className="flex-1 flex justify-between items-center">
            <p className="text-sm text-gray-700">
              Mostrando{" "}
              <span className="font-medium">
                {filteredData.length === 0
                  ? 0
                  : (currentPage - 1) * rowsPerPage + 1}
              </span>{" "}
              -{" "}
              <span className="font-medium">
                {Math.min(currentPage * rowsPerPage, filteredData.length)}
              </span>{" "}
              de <span className="font-medium">{filteredData.length}</span>{" "}
              resultados
            </p>
            <div className="flex gap-2">
              <button
                onClick={goToStart}
                disabled={currentPage === 1}
                className={`px-4 py-2 text-sm rounded-full transition-all duration-200
                  ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-700 hover:shadow-md"
                  }`}
              >
                Inicio
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`px-4 py-2 text-sm rounded-full transition-all duration-200
                  ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-700 hover:shadow-md"
                  }`}
              >
                Anterior
              </button>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
                className={`px-4 py-2 text-sm rounded-full transition-all duration-200
                  ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-700 hover:shadow-md"
                  }`}
              >
                Siguiente
              </button>
              <button
                onClick={goToEnd}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 text-sm rounded-full transition-all duration-200
                  ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-700 hover:shadow-md"
                  }`}
              >
                Final
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modales */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-11/12 max-w-2xl relative p-6 shadow-2xl">
            <button
              onClick={() => setShowModal(false)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors duration-200"
            >
              <IconX size={20} />
            </button>
            <h3 className="text-xl font-semibold mb-4">Comentario</h3>
            <p className="text-gray-600">{selectedComment}</p>
          </div>
        </div>
      )}

      {showInfo && (
        <div className="fixed inset-0 bg-black/70 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-11/12 max-w-2xl relative p-6 shadow-2xl">
            <button
              onClick={() => setShowInfo(false)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors duration-200"
            >
              <IconX size={20} />
            </button>

            <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <IconInfoCircle size={24} className="text-red-600" />
              Guía de Rolplay
            </h2>

            <div className="space-y-4 text-gray-600">
              <div className="flex items-start gap-3">
                <IconReportAnalytics
                  size={20}
                  className="mt-1 text-gray-400 shrink-0"
                />
                <div>
                  <p className="font-medium text-gray-800">Gestión de Tareas</p>
                  <p className="text-sm">
                    Alterna entre "Tareas Completadas" y "Tareas Pendientes"
                    para ver el estado de las actividades
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <IconSearch size={20} className="mt-1 text-gray-400 shrink-0" />
                <div>
                  <p className="font-medium text-gray-800">
                    Búsqueda Inteligente
                  </p>
                  <p className="text-sm">
                    Busca por usuario, actividad, ejercicio o cualquier otro
                    campo disponible
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <IconCalendar
                  size={20}
                  className="mt-1 text-gray-400 shrink-0"
                />
                <div>
                  <p className="font-medium text-gray-800">
                    Filtrado por Período
                  </p>
                  <p className="text-sm">
                    Selecciona un rango de fechas para visualizar actividades
                    específicas
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <i className="fas fa-sort mt-1 text-gray-400"></i>
                <div>
                  <p className="font-medium text-gray-800">
                    Ordenamiento Personalizado
                  </p>
                  <p className="text-sm">
                    Organiza los datos por cualquier columna haciendo clic en su
                    encabezado
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <IconDownload
                  size={20}
                  className="mt-1 text-gray-400 shrink-0"
                />
                <div>
                  <p className="font-medium text-gray-800">
                    Exportación de Reportes
                  </p>
                  <p className="text-sm">
                    Descarga los datos filtrados en formato Excel para su análisis
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros de fecha y botón de descarga */}
      <div className="flex justify-between items-center mt-6">
        <div id="rolplay-date-filter" className="flex items-center gap-4">
          <div className="relative">
            <IconCalendar
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="pl-10 pr-4 py-2.5 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1b2a49] focus:border-transparent text-sm shadow-sm"
              max={endDate || undefined}
            />
          </div>
          <div className="relative">
            <IconCalendar
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="pl-10 pr-4 py-2.5 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1b2a49] focus:border-transparent text-sm shadow-sm"
              min={startDate || undefined}
            />
          </div>
          <button
            onClick={() => {
              if (!startDate && !endDate) {
                toast.error("Selecciona al menos una fecha para filtrar");
                return;
              }
              setActiveStartDate(startDate);
              setActiveEndDate(endDate);
              setCurrentPage(1);
              toast.success("Filtro aplicado", {
                description: "Los resultados han sido filtrados por fecha",
              });
            }}
            className="px-6 py-2.5 bg-[#4b4b4b] text-white rounded-full hover:bg-[#333] transition-all duration-300 text-sm shadow-md hover:shadow-xl flex items-center gap-2"
          >
            <i className="fas fa-filter"></i>
            Filtrar
          </button>
          {(activeStartDate || activeEndDate) && (
            <button
              onClick={() => {
                setStartDate("");
                setEndDate("");
                setActiveStartDate("");
                setActiveEndDate("");
                setCurrentPage(1);
                toast.success("Filtros eliminados", {
                  description: "Se han eliminado los filtros de fecha",
                });
              }}
              className="px-4 py-2.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all duration-300 text-sm shadow-md hover:shadow-xl flex items-center gap-2"
            >
              <i className="fas fa-times"></i>
              Limpiar
            </button>
          )}
        </div>

        <button
          id="rolplay-download"
          onClick={onExportClick}
          className="group px-6 py-3 bg-gradient-to-r from-[#dc2626] to-[#991b1b] text-white rounded-full hover:from-[#991b1b] hover:to-[#dc2626] transition-all duration-300 text-sm shadow-md hover:shadow-xl flex items-center gap-2"
        >
          <IconDownload
            size={18}
            className="group-hover:scale-110 transition-transform"
          />
          Descargar
        </button>
      </div>
    </div>
  );
};

export default RolplaySalinas;
